/**
 * Ponte dos eventos do funil de vendas (OpenPanel) para as gravações de tela.
 *
 * Existe para uma pergunta só: **quais sessões passaram por quais etapas?**
 * Com ela, "me mostre a gravação de quem revisou o pedido e não comprou" vira
 * um filtro; sem ela, o lojista abre gravações no escuro até topar com uma.
 *
 * ## Por que um módulo FOLHA, e não uma chamada direta
 *
 * Três restrições, e este arquivo resolve as três de uma vez:
 *
 * **1. Tempo.** Os gravadores entram em chunk lazy, no idle depois do `load` —
 * e `add_to_cart`/`view_cart` acontecem muito antes disso em boa parte das
 * sessões. Chamar o gravador direto perderia o começo do funil, e perderia
 * MAIS nas sessões curtas, que são justamente as que interessam quando se
 * investiga abandono. Daí o histórico.
 *
 * **2. Dois destinos.** OpenReplay e o gravador rrweb próprio convivem
 * enquanto a paridade não fecha, e carregam de forma independente — a ordem é
 * a do idle, não é nossa. Por isso `assinar()` entrega o HISTÓRICO a cada novo
 * destino: quem chega por último recebe o mesmo começo de funil que quem
 * chegou primeiro.
 *
 * **3. Ciclo de import.** `openPanel.service` → serviço de gravação fecharia
 * um ciclo com `restaurant.service` → `openPanel.service`. O `check:cycles`
 * reprova e o risco de TDZ é real (ver `barrel-core-ciclo-import`). Com este
 * arquivo, as pontas apontam para uma FOLHA — nenhum ciclo.
 *
 * Por isso: **zero imports aqui**, deliberadamente. Mesmo desenho do
 * `sentry-buffer.ts`, pela mesma razão.
 *
 * ## Duas invariantes que não são óbvias
 *
 * - **O teto governa o HISTÓRICO, não a entrega ao vivo.** Passado o evento
 *   50, quem já assinou continua recebendo tudo; só o histórico para de
 *   crescer. O teto existe porque isto é memória de página numa sessão que
 *   pode nunca ser gravada — não para racionar entrega.
 * - **Nada é entregue duas vezes.** Um evento chega ao destino ou ao vivo (se
 *   ele já assinou) ou pelo drain do histórico (se assinou depois), nunca
 *   pelos dois caminhos.
 */

/** Quem entrega o evento. `ts` é o instante REAL, não o do drain. */
export type Destino = (nome: string, props: any, ts: number) => void;

const TETO = 50;

let destinos: Destino[] = [];
let historico: Array<{ nome: string; props: any; ts: number }> = [];
let descartados = 0;

/**
 * Registra um evento do funil. Entrega a quem já assinou e guarda para quem
 * ainda vai assinar. Nunca lança: quem chama é o caminho de compra.
 */
export function registrarEvento(nome: string, props: any): void {
  const ts = Date.now();
  if (historico.length < TETO) historico.push({ nome, props, ts });
  else descartados++;
  // `slice()`: um destino que cancela a própria assinatura de dentro do
  // callback não pode fazer o laço pular o destino seguinte.
  for (const d of destinos.slice()) {
    try {
      d(nome, props, ts);
    } catch (e) { /* telemetria não derruba o fluxo de compra */ }
  }
}

/**
 * Liga um destino. Ele recebe o histórico inteiro na hora — é o que faz o
 * `add_to_cart` de quatro segundos atrás aparecer na gravação — e o vivo
 * depois. Devolve a função que desliga.
 */
export function assinar(fn: Destino): () => void {
  destinos.push(fn);
  for (const e of historico.slice()) {
    try {
      fn(e.nome, e.props, e.ts);
    } catch (err) { /* um evento ruim não pode travar o resto do drain */ }
  }
  return () => {
    destinos = destinos.filter((d) => d !== fn);
  };
}

/** Quantos eventos o histórico jogou fora por teto. Vira metadata na sessão. */
export function totalDescartados(): number {
  return descartados;
}

/** Quantos eventos estão guardados para um destino que ainda não assinou. */
export function tamanhoDaFila(): number {
  return historico.length;
}

/** Quantos destinos ligados. A sonda mostra 0, 1 ou 2. */
export function totalDestinos(): number {
  return destinos.length;
}

/** Só para os testes: devolve o módulo ao estado inicial. */
export function resetarParaTeste(): void {
  destinos = [];
  historico = [];
  descartados = 0;
}
